import React, { FC, ReactNode } from 'react';
import { AccordionDetails, AccordionSummary, Box, styled } from '@mui/material';
import { ThemeTypography } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import { Badge } from 'FeatureModules/DataImport/Components/DataImport.styles';
import { Accordion } from 'DesignSystem/DataDisplay/Accordion/Accordion';
import FlexBox from 'Components/FlexBox';
import { ThemeTypographyColor } from 'DesignSystem/utils/designSystemUtils';

type IIssueBannerProps = {
  content?: ReactNode;
  severity: 'error' | 'warning' | 'info';
  AdditionalSummary?: ReactNode;
  summaryTitle: string;
  count: number;
};

const Container = styled(FlexBox)<{ severity?: 'error' | 'warning' | 'info' }>(
  ({ theme, severity }) => ({
    gap: theme.spacing(2),
    background:
      severity === 'error'
        ? theme.custom.themeColors.error[20]
        : severity === 'warning'
        ? theme.custom.themeColors.accent[20]
        : theme.custom.themeColors.primary[10],
    borderRadius: theme.spacing(1),
    height: 'fit-content',
  })
);

const StyledAccordion = styled(Accordion)(() => ({
  '&.MuiAccordion-root': {
    margin: 0,
    border: 'none !important',
  },
  '& .MuiAccordionDetails-root, & .MuiAccordionDetails-root.Mui-disabled': {
    paddingTop: 0,
    background: 'transparent !important',
  },
}));

const ISSUE_MAP: Record<'error' | 'warning' | 'info', { color: string }> = {
  error: {
    color: 'red-80',
  },
  warning: {
    color: 'yellow-80',
  },
  info: {
    color: 'blue-60',
  },
};

const IssueBanner: FC<IIssueBannerProps> = ({
  content,
  severity,
  summaryTitle,
  AdditionalSummary,
  count,
}) => {
  const summary = (
    <Box display="flex" alignItems="center" gap={1}>
      <Icon
        name={severity === 'info' ? 'info' : 'warning'}
        color={ISSUE_MAP[severity].color}
        size="x-large"
      />
      <Badge severity={severity}>
        <ThemeTypography
          variant="BODY_SMALL_BOLD"
          color={ISSUE_MAP[severity].color.toLocaleLowerCase() as ThemeTypographyColor}
        >
          {count} {summaryTitle}
          {count !== 1 ? 's' : ''}
        </ThemeTypography>
      </Badge>

      {AdditionalSummary}
    </Box>
  );

  if (!content) {
    return (
      <Container p={2} severity={severity}>
        {summary}
      </Container>
    );
  }

  return (
    <Container severity={severity}>
      <StyledAccordion transparent={true} defaultExpanded>
        <AccordionSummary>{summary}</AccordionSummary>
        <AccordionDetails>{content}</AccordionDetails>
      </StyledAccordion>
    </Container>
  );
};

export default IssueBanner;
